import MIcon from "@react-native-vector-icons/material-design-icons"
import { Link } from "expo-router"
import { Platform, Pressable, View } from "react-native"
import { useState } from "react"
import { SafeAreaView } from "react-native-safe-area-context"

import { Header } from "@src/components/Header"
import { ar } from "@src/i18n/ar"
import { trpc } from "utils/trpc"
import { ActivityIndicator, ModalWrapper, Text, TextInput, Button } from "components/core"
import { List } from "@src/components"
import { sharedStyles, formatDateForDisplay } from "@src/utils"

export default function Customer() {
  const [filters, setFilters] = useState<{ name?: string; phone?: string }>({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [tempName, setTempName] = useState("")
  const [tempPhone, setTempPhone] = useState("")

  const customers = trpc.customers.list.useInfiniteQuery(
    { filter: { name: filters.name || undefined, phone: filters.phone || undefined } },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
  )
  const allCustomers = customers.data?.pages.flatMap((page) => page.items) ?? []

  const hasActiveFilters = !!filters.name || !!filters.phone

  const openFilter = () => {
    setTempName(filters.name || "")
    setTempPhone(filters.phone || "")
    setIsFilterOpen(true)
  }

  const applyFilter = () => {
    setFilters({
      name: tempName || undefined,
      phone: tempPhone || undefined,
    })
    setIsFilterOpen(false)
  }

  const clearFilter = () => {
    setTempName("")
    setTempPhone("")
    setFilters({})
    setIsFilterOpen(false)
  }

  const header = (
    <Header
      title={ar.customers}
      right={
        <View className="flex-row gap-2 h-full">
          <Pressable hitSlop={12} onPress={openFilter} className="px-2 h-full justify-center active:opacity-70">
            <MIcon name="filter-variant" size={24} color={hasActiveFilters ? "#3b82f6" : "#4b5563"} />
          </Pressable>
          <Link href="/dashboard/customer/new" asChild>
            <Pressable hitSlop={12} className="px-2 h-full justify-center active:opacity-70">
              <MIcon name="plus" size={24} />
            </Pressable>
          </Link>
          {Platform.OS === "web" && (
            <Pressable hitSlop={12} className="px-2 h-full justify-center active:opacity-70" onPress={() => customers.refetch()} disabled={customers.isPending}>
              <MIcon name="refresh" size={24} />
            </Pressable>
          )}
        </View>
      }
    />
  )

  return (
    <>
      {header}
      <List
        data={allCustomers}
        contentContainerStyle={sharedStyles.contentContainer}
        onRefresh={customers.refetch}
        onEndReached={() => {
          if (customers.hasNextPage && !customers.isFetchingNextPage) customers.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          <>
            {customers.isFetchingNextPage && <ActivityIndicator className="py-4" />}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => {
          const primaryPhone = item.phoneNumbers?.find((p) => p.primary)?.phone || item.phoneNumbers?.[0]?.phone || "No Phone"
          const lastActive = item.lastActiveAt ? formatDateForDisplay(item.lastActiveAt) : null
          return (
            <Link asChild href={`/dashboard/customer/${item.id}`}>
              <Pressable className="p-3 bg-white rounded border border-gray-200 shadow-sm active:opacity-70">
                <View className="flex-row items-center justify-between">
                  <Text className="font-semibold text-base text-gray-800">{item.name}</Text>
                  {/* Reachability status icon, end-aligned with semantic color */}
                  <MIcon
                    name={item.optedIn ? "bell-check" : item.reachable ? "bell-outline" : "bell-off"}
                    size={18}
                    color={item.optedIn ? "#22c55e" : item.reachable ? "#f59e0b" : "#9ca3af"}
                  />
                </View>

                <Text className="text-gray-500 text-sm mt-0.5">{primaryPhone}</Text>

                {/* Meta row, end-aligned: last active */}
                {lastActive && (
                  <View className="flex-row items-center justify-end gap-3 mt-1.5">
                    <View className="flex-row items-center gap-1">
                      <MIcon name="clock-outline" size={13} color="#9ca3af" />
                      <Text className="text-xs text-gray-400">{lastActive}</Text>
                    </View>
                  </View>
                )}
              </Pressable>
            </Link>
          )
        }}
        ListEmptyComponent={
          <View className="p-8 items-center">
            <Text className="text-gray-400">{customers.isPending ? ar.loading : ar.noEmployeesFound /* Using closest 'no found' key */}</Text>
          </View>
        }
      />

      {isFilterOpen && (
        <ModalWrapper onClose={() => setIsFilterOpen(false)}>
          <View className="p-4">
            <Text className="text-lg font-bold mb-4 text-center">{ar.filter}</Text>

            <View className="gap-4">
              <View>
                <Text className="mb-2 text-gray-700">{ar.searchByName}</Text>
                <TextInput placeholder={ar.searchByName} value={tempName} onChangeText={setTempName} />
              </View>

              <View>
                <Text className="mb-2 text-gray-700">{ar.searchByPhone}</Text>
                <TextInput placeholder={ar.searchByPhone} value={tempPhone} onChangeText={setTempPhone} keyboardType="phone-pad" />
              </View>

              <View className="flex-row gap-3 mt-4">
                <View className="flex-1">
                  <Button onPress={clearFilter} style={{ backgroundColor: "#ef4444" }}>
                    {ar.clearFilters}
                  </Button>
                </View>
                <View className="flex-1">
                  <Button onPress={applyFilter}>{ar.apply}</Button>
                </View>
              </View>
            </View>
          </View>
        </ModalWrapper>
      )}
    </>
  )
}
